import api from "@/api";
import { all } from "axios";
import { defineStore } from "pinia";

const endPoint = "/v2/wonde/schools";
const qsParams = "?per_page=200";

interface WondeApiState {
  selectedSchoolId: string | null;
}

interface Teacher {
  id: string;
  forename: string;
  surname: string;
  classes?: { data: Array<any> };
  groups?: { data: Array<any> };
}

interface ClassGroup {
  grade: number;
  gradeClass: string;
  subject: string;
  id: string;
}

export const useWondeApiStore = defineStore({
  id: "wondeApi",
  state: (): WondeApiState => ({
    selectedSchoolId: null,
  }),
  getters: {
    selectedSchoolId: (state): string | null => {
      return state.selectedSchoolId;
    },
  },
  actions: {
    async getSchools() {
      try {
        const response = await api.get(`${endPoint}${qsParams}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching schools:", error);
        throw error;
      }
    },
    async getSchoolEntities(schoolId: string) {
      try {
        const response = await api.get(`${endPoint}/${schoolId}/sync-config`);
        const filtered = this.filterClassGroups(
          response.data.classes,
          response.data.groups
        );
        return {
          teachers: response.data.teachers,
          classGroups: filtered,
        };
      } catch (error) {
        console.error("Error fetching school entities:", error);
        throw error;
      }
    },
    async getClassGroups(schoolId: string): Promise<ClassGroup[]> {
      try {
        const response = await Promise.all([
          this.getAllClasses(schoolId),
          this.getAllGroups(schoolId),
        ]);
        const classes = response[0] ? response[0] : [];
        const groups = response[1] ? response[1] : [];
        const filtered = this.filterClassGroups(classes, groups);
        return filtered;
      } catch (error) {
        console.error("Error fetching class groups:", error);
        throw error;
      }
    },
    async getAllClasses(schoolId: string) {
      let page = 1;
      let allClasses: any[] = [];
      let response;
      do {
        response = await api.get(
          `${endPoint}/${schoolId}/classes${qsParams}&page=${page}`
        );
        allClasses = [...allClasses, ...response.data.data];
        page++;
      } while (response.data?.meta?.pagination?.more === true);
      return allClasses;
    },
    async getAllGroups(schoolId: string) {
      let page = 1;
      let allGroups: any[] = [];
      let response;
      do {
        response = await api.get(
          `${endPoint}/${schoolId}/groups${qsParams}&page=${page}`
        );
        allGroups = [...allGroups, ...response.data.data];
        page++;
      } while (response.data?.meta?.pagination?.more === true);
      return allGroups;
    },

    async getTeachers(schoolId: string): Promise<Teacher[]> {
      try {
        const includes =
          "include=classes,groups,classes.subject,contact_details";
        const response = await api.get(
          `${endPoint}/${schoolId}/employees${qsParams}&${includes}`
        );
        const teachers: Teacher[] = response.data.data;
        const filteredTeachers = teachers.filter(
          (teacher) =>
            (teacher.classes?.data && teacher.classes?.data.length > 0) ||
            (teacher.groups?.data && teacher.groups?.data.length > 0)
        );
        return filteredTeachers;
      } catch (error) {
        console.error("Error fetching teachers:", error);
        throw error;
      }
    },
    async getStudents(schoolId: string) {
      try {
        const response = await api.get(
          `${endPoint}/${schoolId}/students${qsParams}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching students:", error);
        throw error;
      }
    },
    async createSchool(formData: any) {
      try {
        const response = await api.post(
          "/v2/organisation/school/new",
          formData
        );
        return response.data;
      } catch (error) {
        throw new Error("Failed to create school.");
      }
    },
    async triggerJobSync(schoolId: string) {
      try {
        const response = await api.get(`/v2/wonde/sync?schoolId=${schoolId}`);
        if (response.status === 200) return true;
      } catch (error) {
        console.error("Error triggering Wonde Sync Jobs.", error);
        return false;
      }
      return false;
    },
    /**
     * Schools already imported from Wonde and exist in DB
     */
    async getExistingSchools() {
      try {
        const response = await api.get("/v2/wonde/schools/existing/entries");
        return response.data;
      } catch (error) {
        console.log("getExistingSchools error", error);
        return false;
      }
    },
    /**
     * Filter classes and groups from Wonde API
     * 1. Only groups of type REGISTRATION are considered
     * 2. If the name contains a / then the subject is found in the name
     * 3. Only Maths and English classes are considered
     * 4. If the name does not have a grade and letter (11ABC) we skip it
     * 5. If no slash is found try and find the subject from the subject object
     * 6. If the subject object name is null then we default to Maths
     * 7. Only unique class groups are returned
     * @param classes array
     * @param groups array
     * @returns object
     */
    filterClassGroups(classes: Array<any>, groups: Array<any>): ClassGroup[] {
      const allClassGroups = [...classes, ...groups];
      const importantSubjects = ["ma", "ma1", "ma3", "ma4", "ma5", "ma6", "en", "en1", "en2", "en3", "en4" ];
      const filteredData: ClassGroup[] = [];
      for (const item of allClassGroups) {
        let grade: number, gradeClass: string, subject: string;
        if (item.name.includes("/")) {
          const [gradeClassStr, subjectCode] = item.name.split("/");
          const subjectPrefix = subjectCode.slice(0, 3);
          if (!importantSubjects.includes(subjectPrefix.toLowerCase()))
            continue;
          const gradeClassMatch = gradeClassStr.match(/^(\d{1,2})([a-zA-Z]*)$/);     
          if (!gradeClassMatch) continue;
          grade = parseInt(gradeClassMatch[1]);
          gradeClass = gradeClassStr; //gradeClassMatch[2];
          subject = subjectPrefix.slice(0, 2).toLowerCase() === "ma" ? "Maths" : "English";
        } else {
          subject = item.subject ? item.subject.name : "Maths";
          const gradeClassMatch = item.name.match(/^(\d{1,2})([a-zA-Z]*)$/);
          if (!gradeClassMatch) continue;
          grade = parseInt(gradeClassMatch[1]);
          gradeClass = item.name; //gradeClassMatch[2];
        }
        const exists = filteredData.find(
          (d) =>
            d.grade === grade &&
            d.gradeClass === gradeClass &&
            d.subject === subject
        );
        if (!exists) {
          filteredData.push({ grade, gradeClass, subject, id: item.id });
        }
      }
      return filteredData;
    },
  },
});
